import get from 'lodash/get';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type { ConditionalPath, DynamicPath } from '@unifyapps/defs/types/page';
import { useEvaluateConditionalOperator } from '../useComputeConditionEvaluationGetter';
import { isConditionalOperator } from '../../context/DependencyGraphContext/handlers';
import { useGetNoCodeComputedData } from '../computeContext/useGetNoCodeComputedData';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';
import { setIn } from '../../../immutable';
import type { ComputeContextType } from '../computeContext/BuildComputeContext';

export interface UseComputeBlockStateProps {
  blockState: BlockStateUnionType;
  context: ComputeContextType;
  dynPaths?: DynamicPath[];
  condPaths?: ConditionalPath[];
}

export function useComputeBlockStateFromPaths<T>(props: UseComputeBlockStateProps) {
  const { blockState, context, dynPaths = [], condPaths = [] } = props;
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();

  const dynamicPropertyPaths = DependencyHelper.getPathsFromDynamicPaths(dynPaths);
  const conditionalOperatorPaths = DependencyHelper.getPathsFromConditionalPaths(condPaths);

  let newComputedBlockState: unknown = { ...blockState };

  // evaluate the dynamic properties
  dynamicPropertyPaths?.forEach((path) => {
    // if the path is inside a conditional operator, skip evaluating it, because they will be evaluated later in filter evaluator,
    // for example: dynPath: a.b.c.d and condPath: a.b.c, then we should skip evaluating a.b.c.d
    if (conditionalOperatorPaths?.some((condPath) => path.startsWith(condPath))) {
      return;
    }

    const value = get(newComputedBlockState, path) as string | undefined;

    if (typeof value === 'string') {
      const evaluatedValue = getNoCodeComputedData(value, context);
      newComputedBlockState = setIn(newComputedBlockState, path, evaluatedValue) as T;
    } else {
      console.debug(
        `Dynamic property not found at a path: ${path}`,
        blockState.id,
        ` value is: ${value}`,
      );
    }
  });

  // evaluate the filter path
  conditionalOperatorPaths?.forEach((path) => {
    const value = get(newComputedBlockState, path) as ConditionEvaluationObject | undefined;
    if (isConditionalOperator('conditions', value)) {
      const evaluatedValue = evaluateConditionalOperator({
        conditionEvaluationObj: value,
        context,
      });
      newComputedBlockState = setIn(newComputedBlockState, path, evaluatedValue);
    } else if (path !== 'visibility.conditions') {
      console.error(`ConditionalOperator not found at path: ${path}`);
    }
  });

  return newComputedBlockState as T;
}
